import { type RefObject, useCallback, useEffect, useRef } from 'react';

import { type TranscriptSegmentsVirtualizedListHandle } from '~/components/AssetModal/shared/components/TranscriptPanel/components/TranscriptSegmentList/TranscriptSegmentsVirtualizedList';

type UseOnUserListScrollParams = {
  listRef: RefObject<TranscriptSegmentsVirtualizedListHandle>;
  onUserScroll: (event: Event) => void;
};

/**
 * This hook tries to detect if scroll event comes from a user or was made programmatically
 * It returns a function that should be called when scroll event is made programmatically
 *
 * It sets a flag when scroll event is made programmatically and clears it after 50ms
 * Tried to use scrollend event, but:
 * - it is not supported in Safari
 * - it was not reliable - I was getting 'scroll' events after scrollend event
 */
export const useOnUserListScroll = ({ listRef, onUserScroll }: UseOnUserListScrollParams) => {
  const isScrollFromWord = useRef(false);
  const scrollEndTimeout = useRef<number | null>(null);

  const onUserScrollRef = useRef(onUserScroll);
  onUserScrollRef.current = onUserScroll;

  useEffect(() => {
    const scroll = listRef.current?.getScrollElement();

    if (!scroll) return;

    const listener = (event: Event) => {
      if (isScrollFromWord.current) {
        scrollEndTimeout.current && clearTimeout(scrollEndTimeout.current);
        scrollEndTimeout.current = window.setTimeout(() => {
          isScrollFromWord.current = false;
        }, 100);
      } else {
        onUserScrollRef.current(event);
      }
    };

    scroll.addEventListener('scroll', listener);

    return () => {
      scrollEndTimeout.current && clearTimeout(scrollEndTimeout.current);
      scroll.removeEventListener('scroll', listener);
    };
  }, [listRef]);

  const onScrolledProgrammatically = useCallback(() => {
    isScrollFromWord.current = true;
  }, []);

  return {
    onScrolledProgrammatically,
  };
};
